import styles from './MssqlConnection.module.css';

import Card, { CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/Card/Card';
import { useNavigate, useParams } from 'react-router-dom';
import { useBackNavigation } from '../../../hooks/useBackNavigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '../../../lib/toast';
import { configApi } from '../../../lib/apiClient';
import { useAuthStore } from '../../../store/authStore';
import { useConnectionsStore } from '../../../store/connectionsStore';
import { testConnectionApi } from '../../../lib/testConnection';
import SecureInput from '../../../ui/SecureInput/SecureInput';
import ToggleSwitch from '../../../ui/ToggleSwitch/ToggleSwitch';
import { UserRole } from '../../../enums/userRole';
import { hasMinRole } from '../../../lib/permissions';
import { authFieldsSchema } from '../authSections/types';
import SqlAuthSection from '../authSections/SqlAuthSection';
import AzureAdServicePrincipalSection from '../authSections/AzureAdServicePrincipalSection';

const mssqlSchema = z.object({
    name: z.string().min(1, 'Connection name is required'),
    environment: z.enum(['test', 'prod', 'dev']).optional(),
    host: z.string().min(1, 'Host is required'),
    port: z.coerce.number().int().min(1).max(65535).default(1433),
    database: z.string().min(1, 'Database name is required'),
    authType: z.enum(['sql_auth', 'azure_ad_sp']).default('sql_auth'),
    sql_auth: authFieldsSchema.shape.sql_auth,
    azure_ad_sp: authFieldsSchema.shape.azure_ad_sp,
    encrypt: z.boolean().default(true),
    trustServerCertificate: z.boolean().default(false),
});

type MssqlForm = z.infer<typeof mssqlSchema>;

export default function MssqlConnection() {
    const navigate = useNavigate();
    const goBack = useBackNavigation('/connectors');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [config, setConfig] = useState<any | null>(null);
    const user = useAuthStore(state => state.user);
    const { connectors, updateConnection } = useConnectionsStore(state => state);
    const { configId } = useParams();

    const { handleSubmit, setValue, watch, formState: { errors }, setError } = useForm<MssqlForm>({
        resolver: zodResolver(mssqlSchema),
        defaultValues: {
            name: '',
            environment: 'prod' as 'test' | 'prod' | 'dev',
            host: '',
            port: 1433,
            database: '',
            authType: 'sql_auth' as 'sql_auth' | 'azure_ad_sp',
            sql_auth: { username: '', password: '' },
            azure_ad_sp: { tenantId: '', clientId: '', clientSecret: '' },
            encrypt: true,
            trustServerCertificate: false,
        },
    });

    useEffect(() => {
        if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
            navigate('/');
        }
    }, [user]);

    useEffect(() => {
        const configData = connectors.find(c => c.id === configId) || null;
        if (configData) {
            setConfig(configData);
            setValue('name', configData.connectionName || '');
            setValue('environment', (configData.environment as 'test' | 'prod' | 'dev') || 'prod');
            setValue('host', configData.config?.host || '');
            setValue('port', configData.config?.port || 1433);
            setValue('database', configData.config?.database || '');
            setValue('authType', configData.authType === 'azure_ad_sp' ? 'azure_ad_sp' : 'sql_auth');
            setValue('sql_auth', configData.authFields?.sql_auth || { username: '', password: '' });
            setValue('azure_ad_sp', configData.authFields?.azure_ad_sp || { tenantId: '', clientId: '', clientSecret: '' });
            setValue('encrypt', configData.config?.encrypt !== false);
            setValue('trustServerCertificate', configData.config?.trustServerCertificate === true);
        }
    }, [connectors, configId]);

    const onSubmit = async (data: MssqlForm) => {
        if (!data.environment) {
            setError('environment', { type: 'manual', message: 'Environment is required' });
            toast.error('Please select an environment');
            return;
        }

        setSaving(true);
        try {
            const authFields = data.authType === 'azure_ad_sp'
                ? { azure_ad_sp: data.azure_ad_sp }
                : { sql_auth: data.sql_auth };

            const payload = {
                connectionName: data.name,
                environment: data.environment,
                authType: data.authType,
                config: {
                    host: data.host,
                    port: data.port,
                    database: data.database,
                    encrypt: data.encrypt,
                    trustServerCertificate: data.trustServerCertificate,
                },
                authFields,
                userId: user?.id!,
            };

            let response;
            if (!config) response = await configApi.post(payload);
            else response = await configApi.put(config.id, payload);

            setConfig(response);
            updateConnection(response);
            toast.success('Configuration saved successfully!');
        } catch (error: any) {
            toast.error(`Failed to save configuration: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        if (!config?.id) {
            toast.error('Please save configuration first');
            return;
        }

        const formData = watch();
        const authFields = formData.authType === 'azure_ad_sp'
            ? { azure_ad_sp: formData.azure_ad_sp }
            : { sql_auth: formData.sql_auth };

        const connectionData = {
            connectionName: formData.name,
            environment: formData.environment,
            authType: formData.authType,
            config: {
                host: formData.host,
                port: formData.port,
                database: formData.database,
                encrypt: formData.encrypt,
                trustServerCertificate: formData.trustServerCertificate,
            },
            authFields,
        };

        await testConnectionApi(config.id, setTesting, connectionData, user);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>MSSQL Connection</h1>
                    <p>Configure your Microsoft SQL Server or Microsoft Fabric SQL connection</p>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        onClick={goBack}
                        className={styles.cancelBtn}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={testConnection}
                        disabled={testing || !config}
                        className={styles.saveBtn}
                    >
                        {testing ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit(onSubmit)}
                        disabled={saving}
                        className={styles.saveBtn}
                    >
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                {/* BASIC SETTINGS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Basic Settings</CardTitle>
                        <CardDescription>Connection name and environment.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="name"
                                label="Connection Name *"
                                value={watch('name') || ''}
                                onChange={(value) => setValue('name', value)}
                                required
                                className={styles.input}
                                externalError={errors.name?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Environment *</label>
                            <select
                                value={watch('environment') || ''}
                                onChange={(e) => {
                                    setValue('environment', e.target.value as 'test' | 'prod' | 'dev');
                                    setError('environment', { type: 'manual', message: '' });
                                }}
                                className={`${styles.select} ${errors.environment ? styles.selectError : ''}`}
                            >
                                <option value="">Select environment</option>
                                <option value="prod">Prod</option>
                                <option value="test">Test</option>
                                <option value="dev">Dev</option>
                            </select>
                            {errors.environment && (
                                <span className={styles.error}>{errors.environment.message}</span>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* SERVER SETTINGS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Server Settings</CardTitle>
                        <CardDescription>SQL Server host, port, and database name.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.fieldRow}>
                            <SecureInput
                                fieldType="text"
                                label="Host *"
                                value={watch('host') || ''}
                                onChange={(value) => setValue('host', value)}
                                placeholder="localhost, server.database.windows.net, or a Fabric SQL analytics endpoint"
                                description="For Microsoft Fabric, paste the SQL connection string from the SQL analytics endpoint page here (e.g. ....datawarehouse.fabric.microsoft.com)."
                                required
                                className={styles.input}
                                externalError={errors.host?.message}
                            />
                            <SecureInput
                                fieldType="text"
                                label="Port *"
                                value={String(watch('port') || 1433)}
                                onChange={(value) => setValue('port', parseInt(value, 10) || 1433)}
                                placeholder="1433"
                                required
                                className={styles.input}
                                externalError={errors.port?.message}
                            />
                        </div>
                        <div className={styles.field}>
                            <SecureInput
                                fieldType="text"
                                label="Database *"
                                value={watch('database') || ''}
                                onChange={(value) => setValue('database', value)}
                                placeholder="MyDatabase"
                                description="For Microsoft Fabric, this is the Lakehouse or Warehouse name (e.g. DLH_Test)."
                                required
                                className={styles.input}
                                externalError={errors.database?.message}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* AUTHENTICATION */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Authentication</CardTitle>
                        <CardDescription>SQL Server login, or Microsoft Entra ID (required for Microsoft Fabric SQL endpoints).</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.field}>
                            <label className={styles.label}>Authentication Method</label>
                            <select
                                value={watch('authType') || 'sql_auth'}
                                onChange={(e) => setValue('authType', e.target.value as 'sql_auth' | 'azure_ad_sp')}
                                className={styles.select}
                            >
                                <option value="sql_auth">SQL Login</option>
                                <option value="azure_ad_sp">Microsoft Entra ID Service Principal (Fabric)</option>
                            </select>
                        </div>
                        {watch('authType') === 'azure_ad_sp' ? (
                            <AzureAdServicePrincipalSection form={{ watch, setValue }} styles={styles} />
                        ) : (
                            <SqlAuthSection form={{ watch, setValue }} styles={styles} />
                        )}
                    </CardContent>
                </Card>

                {/* CONNECTION OPTIONS */}
                <Card className={styles.section}>
                    <CardHeader>
                        <CardTitle>Connection Options</CardTitle>
                        <CardDescription>Encryption and certificate settings.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className={styles.toggleRow}>
                            <div className={styles.toggleLabel}>
                                <span>Encrypt Connection</span>
                                <span>Use TLS encryption for the connection (recommended for Azure SQL)</span>
                            </div>
                            <ToggleSwitch
                                checked={watch('encrypt')}
                                onChange={(checked) => setValue('encrypt', checked)}
                            />
                        </div>
                        <div className={styles.toggleRow}>
                            <div className={styles.toggleLabel}>
                                <span>Trust Server Certificate</span>
                                <span>Skip server certificate validation (use only for development)</span>
                            </div>
                            <ToggleSwitch
                                checked={watch('trustServerCertificate')}
                                onChange={(checked) => setValue('trustServerCertificate', checked)}
                            />
                        </div>
                    </CardContent>
                </Card>
            </form>
        </div>
    );
}
